/**
 * @file Renders the loading and error states while the app authenticates and authorizes the user.
 */
import Text from "@components/Text";
import { isBackstage } from "@components/Constants";

import AuthorizationError from "./AuthorizationError";

const LABEL_AUTHORIZING = "Authorizing...";

// The store carries more than this. These are the members that decide what
// this component renders.
type AuthorizationStore = {
  isLoading: boolean;
  isAuthorized: boolean;
  authorizationError?: string;
  authenticationError?: string;
};

type Props = {
  authorizationStore: AuthorizationStore;
};

/**
 * Renders the authentication or authorization error, a loading message, or nothing once authorized.
 */
const Authorization = ({
  authorizationStore,
}: Props) => {
  const {
    isLoading,
    isAuthorized,
    authorizationError,
    authenticationError,
  } = authorizationStore;

  const hasAuthenticationError = !!authenticationError;
  const hasAuthorizationError = !!authorizationError;

  // Authentication fails before the app can authorize, and the loading state
  // never ends in that case, therefore this comes before the loading state.
  if (hasAuthenticationError) {
    return (
      <AuthorizationError
        message={authenticationError}
        isBackstage={isBackstage}
      />
    );
  }

  if (isLoading) {
    return <Text text={LABEL_AUTHORIZING} />;
  }

  if (!isAuthorized) {
    if (hasAuthorizationError) {
      return (
        <AuthorizationError
          isBackstage={isBackstage}
        />
      );
    }

    return <Text text={LABEL_AUTHORIZING} />;
  }

  return null;
};

export default Authorization;
